import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import GradientButton from '../../components/GradientButton'
import { 
  FaCheckCircle,
  FaStar,
  FaCalendarAlt,
  FaBriefcase,
  FaClock,
  FaUmbrellaBeach,
  FaMoneyBillWave,
  FaFileAlt,
  FaComments,
  FaShieldAlt,
  FaGraduationCap,
  FaUsers,
  FaLightbulb,
  FaReply
} from 'react-icons/fa';

const Pricing = () => {
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const [scope, setScope] = useState('main'); // 'main' | 'expert'
  const [billingPeriod, setBillingPeriod] = useState(1); // ⬅️ domyślnie 1 miesiąc, nie 2
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mainPlans, setMainPlans] = useState(null);   // ⬅️ osobno
  const [expertPlans, setExpertPlans] = useState(null); // ⬅️ osobno
  const [userSubscription, setUserSubscription] = useState(null);
  const [isFromMobile, setIsFromMobile] = useState(false);
  const [shouldReturnToApp, setShouldReturnToApp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;
  
  const isInApp = location.pathname === '/app-pricing';
  const isAuthenticated = !!userInfo;

  // Obsługuje parametry z aplikacji mobilnej
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromMobile = searchParams.get('from') === 'mobile';
    const planParam = searchParams.get('plan');
    const shouldReturn = searchParams.get('return_to_app') === 'true';
    
    if (fromMobile) {
      setIsFromMobile(true);
      setShouldReturnToApp(shouldReturn);
      
      if (planParam) {
        setBillingPeriod(parseInt(planParam));
      }
    }
  }, [location.search]);

  // Reset loading state when component mounts (important for browser back button from PayU)
  useEffect(() => {
    const wasRedirected = sessionStorage.getItem('payment_redirect');
    if (wasRedirected) {
      sessionStorage.removeItem('payment_redirect');
      setIsLoading(false);
      setError('');
      
      if (shouldReturnToApp) {
        setTimeout(() => {
          window.location.href = 'sigmawork://payment-return';
        }, 2000);
      }
    } else {
      setIsLoading(false);
      setError('');
    }
  }, [shouldReturnToApp]);

  useEffect(() => {
    setIsLoading(false);
    setError('');
  }, [location.pathname]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setIsLoading(false);
        setError('');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Pobiera plany + subskrypcję użytkownika, zależnie od wybranego "scope" (main/expert)
useEffect(() => {
  let isCancelled = false;

  const fetchAllPlans = async () => {
    if (!userInfo || !userInfo.token) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    try {
      const userType = userInfo.user_type || 'worker';

      const buildPlansMap = async (scopeValue) => {
        const url = `${API_BASE}/api/subscription/plans/?user_type=${userType}&scope=${scopeValue}`;
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userInfo.token}`
          }
        });
        const responseText = await response.text();
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error(`Otrzymano nieprawidłowy format danych. Content-Type: ${contentType}`);
        }

        const data = JSON.parse(responseText);
        if (!Array.isArray(data)) throw new Error("API nie zwróciło tablicy planów");
        if (data.length === 0) throw new Error("Brak aktywnych planów w bazie danych");

        const map = {};
        data.forEach(plan => {
          map[plan.plan_type] = {
            id: plan.id,
            title: plan.title || plan.name,
            subtitle: plan.subtitle || '',
            price: plan.price,
            period: plan.duration_months === 1 ? '1 miesiąc' :
                    plan.duration_months === 12 ? '12 miesięcy' :
                    plan.duration_months === 24 ? '24 miesiące' :
                    plan.duration_months === 36 ? '36 miesięcy' :
                    `${plan.duration_months} miesięcy`,
            discount: plan.discount_percentage > 0 ? `${plan.discount_percentage}%` : null,
            popular: plan.plan_type === 4,
            gradient: getGradientForPlan(plan.plan_type),
            color: getColorForPlan(plan.plan_type),
            badge: scopeValue === 'expert' ? null : getBadgeForPlan(plan.plan_type), // ⬅️ zmiana
            duration_months: plan.duration_months
          };
        });
        return map;
      };

      // ⬅️ oba scope'y pobierane RAZ, równolegle
      const [mainMap, expertMap] = await Promise.all([
        buildPlansMap('main'),
        buildPlansMap('expert')
      ]);

      if (isCancelled) return;

      setMainPlans(mainMap);
      setExpertPlans(expertMap);

      const userUrl = API_BASE ? `${API_BASE}/api/users/profile/` : '/api/users/profile/';
      const userResponse = await fetch(userUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userInfo.token}`
        }
      });

      if (isCancelled) return;

      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.subscription) {
          setUserSubscription(userData.subscription);
        }
      }

    } catch (err) {
      if (!isCancelled) {
        console.error('❌ Błąd pobierania planów:', err);
        setError(`Nie udało się pobrać planów cenowych: ${err.message}`);
      }
    }
  };

  fetchAllPlans();
  return () => { isCancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [userInfo, userInfo?.user_type, navigate, location.pathname, API_BASE]); // ⬅️ scope USUNIĘTY z zależności

  const getGradientForPlan = (planType) => {
    const gradients = {
      1: 'linear-gradient(135deg, #21D4FD 0%, #B721FF 100%)',
      2: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
      3: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      4: 'linear-gradient(135deg, #21D4FD 0%, #B721FF 100%)'
    };
    return gradients[planType] || gradients[2];
  };

  const getColorForPlan = (planType) => {
    const colors = { 1: 'purple', 2: 'blue', 3: 'pink', 4: 'purple' };
    return colors[planType] || 'blue';
  };

  const getBadgeForPlan = (planType) => {
    const badges = {
      1: '💰 PODSTAWOWY',
      2: null,
      3: '🔥 NAJPOPULARNIEJSZY',
      4: '👑 NAJLEPSZA WARTOŚĆ'
    };
    return badges[planType] || null;
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Sprawdza aktywność subskrypcji DLA WYBRANEGO SCOPE (main albo expert)
  const isPlanActive = () => {
    if (!userSubscription) return false;

    const subData = scope === 'expert' ? userSubscription.expert : userSubscription.main;
    if (!subData) return false;
    if (subData.plan && subData.plan.toLowerCase() === 'darmowy') return false;
    if (!subData.is_active || !subData.end_date) return false;

    return new Date() < new Date(subData.end_date);
  };

  const getActiveEndDate = () => {
    const subData = scope === 'expert' ? userSubscription?.expert : userSubscription?.main;
    return subData?.end_date || null;
  };

  const featureIcons = {
    'Obliczanie puli urlopowej': <FaUmbrellaBeach />,
    'Historia urlopów': <FaCalendarAlt />,
    'Historia zatrudnienia': <FaBriefcase />,
    'Czas pracy': <FaClock />,
    'Święta i dni wolne': <FaStar />,
    'Okresy wypowiedzenia': <FaFileAlt />,
    'Wynagrodzenia - stawki + kalkulator': <FaMoneyBillWave />,
    'Wzory dokumentów': <FaFileAlt />,
    'Komunikacja i stres': <FaComments />,
    'BHP i sytuacje kryzysowe': <FaShieldAlt />,
    'CV & start kariery': <FaGraduationCap />,
    'Wsparcie i społeczność': <FaUsers />,
    'Konsultacja z ekspertem prawa pracy': <FaLightbulb />,
    'Odpowiedź w ciągu 48 godzin': <FaReply />,
  };

  const mainFeatures = [
    'Historia urlopów',
    'Historia zatrudnienia',
    'Czas pracy',
    'Święta i dni wolne',
    'Okresy wypowiedzenia',
    'Wzory dokumentów',
    'CV & start kariery',
    'Wsparcie i społeczność'
  ];

  const expertFeatures = [
    'Konsultacja z ekspertem prawa pracy',
    'Odpowiedź w ciągu 48 godzin',
  ];

  const currentFeatures = scope === 'expert' ? expertFeatures : mainFeatures;

  // ⬅️ NOWE: kategoria (plan_type) planu eksperckiego, spójna z tym co wysyła "main"
  const expertPlanType = expertPlans ? Number(Object.keys(expertPlans)[0]) : null;

  const handleSelectPlan = async () => {
    setError('');

    if (!isAuthenticated) {
      localStorage.setItem('selectedPlan', billingPeriod.toString());
      navigate('/login', { state: { from: '/pricing', selectedPlan: billingPeriod } });
      return;
    }

    if (!userInfo || !userInfo.token) {
      setError('Sesja wygasła. Zaloguj się ponownie.');
      navigate('/login');
      return;
    }

    setIsLoading(true);

    try {
      const paymentUrl = API_BASE ? `${API_BASE}/api/payment/create/` : '/api/payment/create/';
      const response = await fetch(paymentUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userInfo.token}`
        },
        body: JSON.stringify({
          plan_id: selectedPlan.id,
          is_mobile: isFromMobile,
          return_to_app: shouldReturnToApp
        })
      });

      const data = await response.json();

      if (response.ok && data.success && data.redirectUri) {
        sessionStorage.setItem('payment_redirect', 'true');
        window.location.href = data.redirectUri;
      } else {
        setError(data.error || 'Wystąpił błąd podczas tworzenia płatności');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Błąd płatności:', error);
      setError('Wystąpił błąd połączenia. Spróbuj ponownie później.');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isInApp && isAuthenticated) {
      const savedPlan = localStorage.getItem('selectedPlan');
      if (savedPlan) {
        setBillingPeriod(parseInt(savedPlan));
        localStorage.removeItem('selectedPlan');
      }
    }
  }, [isInApp, isAuthenticated]);

  if (!mainPlans || !expertPlans) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // ⬅️ NOWE: zabezpieczenie, gdyby billingPeriod nie istniał w mainPlans
  const mainKeys = Object.keys(mainPlans).map(Number).sort((a, b) => a - b);
  const effectiveBillingPeriod = mainPlans[billingPeriod] ? billingPeriod : mainKeys[0];

  // selectedPlan liczony inaczej w zależności od scope
  const selectedPlan = scope === 'expert'
    ? Object.values(expertPlans)[0]   // ekspert ma tylko jeden wariant
    : mainPlans[effectiveBillingPeriod]; // ⬅️ zmiana: effectiveBillingPeriod zamiast billingPeriod

  if (!selectedPlan) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      overflowX: 'clip',
      width: '100%'
    }}>
      <Container fluid className="px-2 px-md-4 scalable-column">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-5"
        >
          <h1 className="display-3 fw-bold text-white mb-3">
            Proste ceny, wielkie możliwości
          </h1>
          <p className="lead text-white-50" style={{ fontSize: '1.3rem' }}>
            Wybierz plan dopasowany do Twoich potrzeb
          </p>
        </motion.div>

        {error && (
          <Row className="justify-content-center mb-4">
            <Col xs={12} lg={8}>
              <Alert variant="danger" onClose={() => setError('')} dismissible>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        {/* PRZEŁĄCZNIK PRODUKTU: Pakiet główny / Zapytaj eksperta */}
        <div className="d-flex justify-content-center gap-2 mb-4">
          <button
            type="button"
            className="btn"
            onClick={() => setScope('main')}
            style={{
              borderRadius: '15px',
              border: 'none',
              padding: '10px 24px',
              fontWeight: 'bold',
              background: scope === 'main' ? 'var(--pub-navy)' : '#fff',
              color: scope === 'main' ? '#fff' : 'var(--pub-navy)',
              boxShadow: 'inset 0 0 0 1px var(--pub-navy)',
            }}
          >
            Pakiet główny
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => setScope('expert')}
            style={{
              borderRadius: '15px',
              border: 'none',
              padding: '10px 24px',
              fontWeight: 'bold',
              background: scope === 'expert' ? 'var(--pub-navy)' : '#fff',
              color: scope === 'expert' ? '#fff' : 'var(--pub-navy)',
              boxShadow: 'inset 0 0 0 1px var(--pub-navy)',
            }}
          >
            Zapytaj eksperta
          </button>
        </div>

        {/* PRZEŁĄCZNIK OKRESU — tylko dla pakietu głównego (expert ma jeden wariant 30-dniowy) */}
        {scope === 'main' && (
          <motion.div
            {...fadeInUp}
            className="text-center mb-5"
          >
            <Card 
              className="border-0 shadow-lg d-inline-block"
              style={{
                borderRadius: '25px',
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(10px)',
                padding: '0.5rem'
              }}
            >
              <div className="d-flex gap-2">
                {[1, 2, 3, 4].map((period) => {
                  const plan = mainPlans[period];
                  if (!plan) return null;
                  return (
                    <button
                      type="button"
                      className="btn px-4 py-3"
                      key={period}
                      onClick={() => setBillingPeriod(period)}
                      style={{
                        borderRadius: '20px',
                        border: 'none',
                        background: effectiveBillingPeriod === period
                          ? 'var(--pub-navy)'
                          : '#fff',
                        color: effectiveBillingPeriod === period ? '#fff' : 'var(--pub-navy)',
                        boxShadow: 'inset 0 0 0 1px var(--pub-navy)',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        transition: 'all 0.3s ease',
                        minWidth: window.innerWidth >= 768 ? '120px' : '100px',
                        position: 'relative'
                      }}
                    >
                      {plan.duration_months} {plan.duration_months === 1 ? 'mies.' : 'mies.'}
                      {plan.discount && (
                        <div 
                          className="position-absolute"
                          style={{
                            top: '-10px',
                            right: '-10px',
                            background: '#f5576c',
                            borderRadius: '10px',
                            padding: '2px 8px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold'
                          }}
                        >
                          -{plan.discount}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        )}

        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={8}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${scope}-${billingPeriod}`}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <Card 
                  className="border-0 shadow-lg"
                  style={{
                    borderRadius: '30px',
                    overflow: 'hidden',
                    position: 'relative'
                  }}
                >
                  {selectedPlan.badge && (
                    <div 
                      className="position-absolute w-100 text-center py-2"
                      style={{
                        top: 0,
                        background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                        zIndex: 10,
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        color: 'white'
                      }}
                    >
                      {selectedPlan.badge}
                    </div>
                  )}

                  <div 
                    className="p-5"
                    style={{ 
                      background: 'var(--pub-bg-alt)',
                      paddingTop: selectedPlan.badge ? '4rem' : '3rem'
                    }}
                  >
                    <Row className="align-items-center">
                      <Col xs={12} md={7} className="text-center text-md-start mb-4 mb-md-0">
                        <h2 className="fw-bold mb-2" style={{ fontSize: '2.5rem' }}>
                          {selectedPlan.title}
                        </h2>
                        <p className="mb-0 opacity-75" style={{ fontSize: '1.2rem' }}>
                          {selectedPlan.subtitle}
                        </p>
                      </Col>
                      <Col xs={12} md={5} className="text-center">
                        <div>
                          {selectedPlan.duration_months !== 1 && (
                            <div className="mb-3" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '4px' }}>
                              <span style={{ fontSize: '3.5rem', fontWeight: 'bold', opacity: 0.95 }}>
                                {(selectedPlan.price / selectedPlan.duration_months).toFixed(2)} zł
                              </span>
                              <span style={{ fontSize: '1.2rem', fontWeight: 'bold', opacity: 0.95 }}>
                                / miesiąc
                              </span>
                            </div>
                          )}
                          <div className="d-flex align-items-baseline justify-content-center">
                            <span style={{ fontSize: selectedPlan.duration_months === 1 ? '4rem' : '2rem', fontWeight: 'bold' }}>
                              {selectedPlan.price}
                            </span>
                            <span style={{ fontSize: '1.5rem' }} className="ms-2">
                              zł
                            </span>
                          </div>
                          <p className="mb-0" style={{ fontSize: '0.9rem' }}>
                            za {selectedPlan.period}
                          </p>
                          {selectedPlan.discount && (
                            <div 
                              className="mt-2 d-inline-block px-3 py-1"
                              style={{
                                background: 'rgba(255,255,255,0.2)',
                                borderRadius: '15px',
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                              }}
                            >
                              Oszczędzasz {selectedPlan.discount}!
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <Card.Body className="p-4 p-md-5">
                    <h4 className="fw-bold mb-4 text-center">
                      Co otrzymujesz:
                    </h4>
                    <Row className="g-3">
                      {currentFeatures.map((feature, index) => (
                        <Col key={index} xs={12} md={6}>
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="d-flex align-items-center p-3"
                            style={{
                              background: 'rgba(102, 126, 234, 0.05)',
                              borderRadius: '15px'
                            }}
                          >
                            <div 
                              className="me-3 flex-shrink-0"
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '12px',
                                background: selectedPlan.gradient,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white'
                              }}
                            >
                              {featureIcons[feature]}
                            </div>
                            <span style={{ fontSize: '1rem', fontWeight: '500' }}>
                              {feature}
                            </span>
                          </motion.div>
                        </Col>
                      ))}
                    </Row>

                    <div className="text-center mt-5">
                      {isPlanActive() ? (
                        <div>
                          <Button
                            disabled
                            size="lg"
                            style={{
                              background: '#6c757d',
                              border: 'none',
                              padding: '15px 40px',
                              fontSize: '1.2rem',
                              fontWeight: 'bold',
                              borderRadius: '15px',
                              cursor: 'not-allowed',
                              opacity: 0.7
                            }}
                          >
                            ✓ Plan aktywny
                          </Button>
                          <div className="mt-3 p-3" style={{
                            background: 'rgba(76, 175, 80, 0.1)',
                            border: '1px solid #4caf50',
                            borderRadius: '15px',
                            color: '#4caf50',
                            fontWeight: '500'
                          }}>
                            <strong>Plan aktywny do:</strong> {new Date(getActiveEndDate()).toLocaleDateString('pl-PL', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                      ) : (
                        <>
                          {isFromMobile && (
                            <Alert variant="info" className="mb-3" style={{ borderRadius: '15px' }}>
                              <Alert.Heading className="mb-2">
                                📱 Aplikacja mobilna
                              </Alert.Heading>
                              <p className="mb-0 small">
                                Po zakupie subskrypcji zostaniesz automatycznie przekierowany do aplikacji.
                              </p>
                            </Alert>
                          )}
                          <GradientButton
                            onClick={handleSelectPlan}
                            disabled={isLoading}
                            gradient={selectedPlan.color}
                            style={{
                              padding: '15px 40px',
                              fontSize: '1.2rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {isLoading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Przetwarzanie...
                              </>
                            ) : (
                              <>Wybierz {selectedPlan.title}</>
                            )}
                          </GradientButton>
                        </>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </AnimatePresence>
          </Col>
        </Row>

        <motion.div
          {...fadeInUp}
          className="my-5 py-5"
        >
          <Row className="g-4">
            <Col xs={12} md={4}>
              <Card 
                className="border-0 shadow h-100 text-center"
                style={{
                  borderRadius: '25px',
                  background: 'rgba(102, 126, 234, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Card.Body className="p-4">
                  <div style={{ fontSize: '3rem' }} className="mb-3">🔒</div>
                  <h5 className="text-white fw-bold mb-3">Bezpieczne płatności</h5>
                  <p className="text-white-50 mb-0">
                    Wszystkie transakcje są szyfrowane i zabezpieczone
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card 
                className="border-0 shadow h-100 text-center"
                style={{
                  borderRadius: '25px',
                  background: 'rgba(240, 147, 251, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Card.Body className="p-4">
                  <div style={{ fontSize: '3rem' }} className="mb-3">⚡</div>
                  <h5 className="text-white fw-bold mb-3">Natychmiastowy dostęp</h5>
                  <p className="text-white-50 mb-0">
                    Wszystkie funkcje dostępne od razu po zakupie
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card 
                className="border-0 shadow h-100 text-center"
                style={{
                  borderRadius: '25px',
                  background: 'rgba(79, 172, 254, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Card.Body className="p-4">
                  <div style={{ fontSize: '3rem' }} className="mb-3">💬</div>
                  <h5 className="text-white fw-bold mb-3">Wsparcie 24/7</h5>
                  <p className="text-white-50 mb-0">
                    Nasz zespół zawsze służy pomocą
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>

        <hr />

        <motion.div
          {...fadeInUp}
          className="my-5 py-4"
        >
          <div className="text-center mb-5">
            <h2 className="text-white fw-bold mb-3" style={{ fontSize: '2.5rem' }}>
              Masz pytania?
            </h2>
          </div>

          <Row className="g-4">
            {[
              {
                q: "Czy mogę korzystać z aplikacji na różnych urządzeniach?",
                a: "Oczywiście! Nasza platforma działa na komputerze, tablecie i telefonie — wystarczy zalogować się na swoje konto."
              },
              {
                q: "Co się stanie po zakończeniu subskrypcji?",
                a: "Zachowasz dostęp do swoich danych, ale niektóre funkcje będą ograniczone. Możesz odnowić w każdej chwili."
              },
              {
                q: "Czy są jakieś dodatkowe opłaty?",
                a: "Nie! Cena, którą widzisz, to wszystko czego potrzebujesz. Bez ukrytych kosztów."
              },
              {
                q: "Czy dostanę fakturę?",
                a: "Oczywiście! Faktura zostanie wysłana na Twój email."
              }
            ].map((faq, index) => (
              <Col key={index} xs={12} md={6}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className="border-0 shadow h-100"
                    style={{
                      borderRadius: '20px',
                      background: 'rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <Card.Body className="p-4">
                      <h5 className="text-white fw-bold mb-3">
                        <FaCheckCircle className="me-2" style={{ color: '#43e97b' }} />
                        {faq.q}
                      </h5>
                      <p className="text-white-50 mb-0" style={{ fontSize: '1.05rem' }}>
                        {faq.a}
                      </p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>

      </Container>
    </div>
  );
};

export default Pricing;